import { getElapsedIdle } from 'app/containers/RetirementApp/IdletimeManager'
import { ActionCreator } from 'app/redux/ActionCreator'
import { ActionTypes, IUserAction, IUserState } from 'app/redux/User/reducer'
import { AccountsService, IsValidAuthTokenResponse, LoginResponse } from 'app/services/AccountsService'
import { OperationStatus } from 'app/services/OperationStatus'
import { genericFailure } from 'app/services/WebServiceBase'
import { makeDebug } from 'app/utils'
import { getWebServicesConfig } from 'config'
import * as moment from 'moment'

const log = makeDebug(false)

const maxIdleAllowingAutologin = moment.duration(2, 'hours')
//const maxIdleAllowingAutologin = moment.duration(20, 'minutes')

class UserActionCreator extends ActionCreator<IUserState, IUserAction> {
  private static LOCAL_STORAGE_KEY = 'U$3R'
  private static initStarting = { type: ActionTypes.APP_INIT_STARTING }
  private static initComplete = { type: ActionTypes.APP_INIT_COMPLETED }

  private AccountService = new AccountsService(getWebServicesConfig().accountsEndpoint)

  /**
   * Initialize this portion of the app. When done, post an action that sets 'isInit'
   * property to true.
   */
  init() {
    return this.dispatchAsync(
      () => {
        const user = this.getUserFromStorage()
        const initCompletePromise = Promise.resolve<IUserAction>(UserActionCreator.initComplete)
        let transactionPromise = Promise.resolve<any>(0)

        const elapsedIdle = getElapsedIdle().as('seconds')
        log('user has been idle for ' + elapsedIdle + 'secs')
        const idleTooLong = elapsedIdle > maxIdleAllowingAutologin.as('seconds')
        if (idleTooLong) {
          log('user has been idle for too long- ' + elapsedIdle + 'secs')
        }

        if (!idleTooLong && user && user.isInit) {
          // we have an existing login. Therefore we need to ensure that the access
          // token is [still] valid

          transactionPromise = this.reauthorize(user).catch((reason) => {
            log('The existing access token for user: ' + user.username + ' is not valid')
          })
        }

        return transactionPromise.then(() => initCompletePromise)
      })
      .then(() => {
        const user = this.getUserFromStorage()

        if (user) {
          this.setSharedVar('accessToken', user.accessToken)
          return user.accessToken
        }

        return null
      })
  }

  login(username: string, password: string): Promise<IUserAction> {

    this.dispatch(this.loginStarting(username))

    return this.AccountService.login(username, password)
      .then((lr) => {
        // the server may have logged us in as someone else
        // this would typically be done for impersonation support
        if (lr.RealEmail) { username = lr.RealEmail }
        return this.loginWithToken(username, lr.AccessToken, lr)
      })
      .catch((reason) => this.loginFailed(username, reason))
  }

  loginWithToken(username: string, accessToken: string, response?: LoginResponse): IUserAction {
    const isInit = true
    const op = response ? OperationStatus.fromResponse(response) : new OperationStatus()
    log('loginWithToken response: ' + JSON.stringify(response))
    const userID = response.UserID

    this.storeUserInfo(username, userID, accessToken)
    this.setSharedVar('accessToken', accessToken)

    return this.dispatch({
      type: ActionTypes.LOGIN_COMPLETED,
      user: { userID, username, accessToken, isInit, op }
    })
  }

  logout() {
    try {
      localStorage.setItem(UserActionCreator.LOCAL_STORAGE_KEY, null)
    } catch (e) {
      // tslint:disable-next-line:no-console
      console.error('Unable to log user out')
    }

    return this.dispatch({ type: ActionTypes.LOGOUT })
  }

  protected storeUserInfo(username: string, userID: string, accessToken: string) {
    try {
      const user: IUserState = {
        username,
        userID,
        accessToken,
        isInit: true,
        op: undefined
      }
      const userStr = JSON.stringify(user)
      localStorage.setItem(UserActionCreator.LOCAL_STORAGE_KEY, userStr)
    } catch (e) {
      // tslint:disable-next-line:no-console
      console.error('Unable to save user credentials')
    }
  }

  protected reauthorize(user: IUserState): Promise<IUserAction> {

    this.dispatch(this.isAuthenticatedStarting(user.username))

    return this.dispatchAsync((getState) => {

      return this.AccountService.isValidAuthToken(user.accessToken)
        .then((vr: IsValidAuthTokenResponse) => {
          log('isVlidAuthToken response: ' + JSON.stringify(vr))
          return this.isAuthenticatedCompleted(user.username, vr)
        })
        .catch((reason) => this.isAuthenticatedFailed(user.username, reason))
    })
  }

  private getUserFromStorage() {
    try {
      const userJSONStr = localStorage.getItem(UserActionCreator.LOCAL_STORAGE_KEY)
      return JSON.parse(userJSONStr) as IUserState
    } catch (reason) {
      return null
    }
  }

  private loginFailed(username: string, reason: any): IUserAction {
    return this.dispatch({
      type: ActionTypes.LOGIN_FAILED,
      user: { username, op: genericFailure(reason) }
    })
  }

  private isAuthenticatedStarting(username: string): IUserAction {
    return {
      type: ActionTypes.IS_AUTH_STARTING,
      user: { username, op: new OperationStatus().setStarting('isAuthenticated starting') }
    }
  }

  private isAuthenticatedCompleted(username: string, response: IsValidAuthTokenResponse): IUserAction {
    const userID = response.UserID
    return {
      type: ActionTypes.IS_AUTH_COMPLETED,
      user: { userID, username, accessToken: response.AccessToken, op: OperationStatus.fromResponse(response) }
    }
  }

  private isAuthenticatedFailed(username: string, reason: any): IUserAction {
    return {
      type: ActionTypes.IS_AUTH_FAILED,
      user: { username, op: genericFailure(reason) }
    }
  }

  private loginStarting = (username: string): IUserAction => {
    return {
      type: ActionTypes.LOGIN_STARTING,
      user: { username, op: new OperationStatus().setStarting('login starting') }
    }
  }

}

const UserActions = new UserActionCreator()

export {
  UserActions,
  UserActionCreator
}



// WEBPACK FOOTER //
// ./src/app/redux/User/actions/index.ts